import { useConfigurator } from '@/app/store/configurator/hooks/use-configurator';
import { sectionParams } from '@/shared/constants/section';
import { getConfiguratorTitle } from '@/shared/lib/configurator';
import { formatNumber } from '@/shared/lib/number';
import { calculatePrice } from '@/shared/lib/price';
import { getSectionParams } from '@/shared/lib/section';
import { Section } from '@/shared/types/configurator';
import { useConfiguratorData } from '@/widgets/configurator';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../../shared/hooks/use-toast';
import {
	PDF_FILE_NAME,
	PDF_FONT_SIZES,
	PDF_SECTIONS_HORIZONTAL_GAP,
	PDF_SECTIONS_PER_LINE,
	PDF_SECTIONS_VERTICAL_GAP,
	PDF_X_PADDING,
	PDF_Y_PADDING,
} from '../constants/pdf';
import { PdfGenerator } from '../lib/pdf';

const { handleShowToast } = useToast.getState();

export const useExportPdf = () => {
	const [isLoading, setIsLoading] = useState(false);
	const { state } = useConfigurator();
	const {
		data: { prices },
	} = useConfiguratorData();
	const { t } = useTranslation(undefined);

	const handleExportPDF = async () => {
		if (isLoading) return;

		try {
			setIsLoading(true);

			const pdfGenerator = new PdfGenerator({
				xPadding: PDF_X_PADDING,
				yPadding: PDF_Y_PADDING,
				compress: true,
			});
			const fileSize = pdfGenerator.getFileSize();

			await pdfGenerator.addFonts();

			// Add title
			pdfGenerator.addText({
				fontSize: PDF_FONT_SIZES.LARGE,
				text: t('pdf.title'),
				fontStyle: 'bold',
			});

			// Add subtitle
			pdfGenerator.addText({
				topMargin: 5,
				fontSize: PDF_FONT_SIZES.MEDIUM,
				text: getConfiguratorTitle(state),
			});

			// Add sections
			const sectionWidth =
				(fileSize.contentWidth -
					(PDF_SECTIONS_PER_LINE - 1) * PDF_SECTIONS_HORIZONTAL_GAP) /
				PDF_SECTIONS_PER_LINE;
			const groupedSections = state.sections.reduce<Section[][]>((acc, section) => {
				if (!acc.length) {
					acc.push([]);
				}

				const lastSection = acc[acc.length - 1];
				if (lastSection.length < PDF_SECTIONS_PER_LINE) {
					lastSection.push(section);
				} else {
					acc.push([section]);
				}

				return acc;
			}, []);

			for (let i = 0; i < groupedSections.length; i++) {
				if (i === 0) {
					// Add top margin before sections
					pdfGenerator.updateYPosition(15);
				}

				const group = groupedSections[i];
				const sectionHeight = sectionParams.size * 10 + 15 + PDF_SECTIONS_VERTICAL_GAP;
				const yPosition = pdfGenerator.getYPosition(sectionHeight);

				for (let j = 0; j < group.length; j++) {
					const sectionOptions = getSectionParams(group[j]);
					const xPosition =
						PDF_X_PADDING + j * (sectionWidth + PDF_SECTIONS_HORIZONTAL_GAP);

					pdfGenerator.addText({
						text: `${t('sectionParams.sectionUnit')} ${i * PDF_SECTIONS_PER_LINE + j + 1}`,
						fontSize: PDF_FONT_SIZES.MEDIUM,
						xPosition,
						yPosition,
					});

					for (let index = 0; index < sectionOptions.length; index++) {
						const [key, value] = sectionOptions[index];

						const keyStyle = {
							fontSize: PDF_FONT_SIZES.SMALL,
							fontStyle: 'bold',
						} as const;
						const valueStyle = {
							fontSize: PDF_FONT_SIZES.SMALL,
						} as const;

						const keyTextWidth = pdfGenerator.getTextSize({
							text: `${key}:`,
							...keyStyle,
						});
						const valueTextWidth = pdfGenerator.getTextSize({
							text: value,
							...valueStyle,
						});

						const textYPosition = yPosition + 15 + index * 10;
						const valueXPosition =
							xPosition +
							keyTextWidth.width +
							(sectionWidth - keyTextWidth.width - valueTextWidth.width);

						pdfGenerator.addText({
							text: `${key}:`,
							xPosition,
							yPosition: textYPosition,
							...keyStyle,
						});

						pdfGenerator.addText({
							text: value,
							yPosition: textYPosition,
							xPosition: valueXPosition,
							...valueStyle,
						});
					}
				}

				pdfGenerator.updateYPosition(sectionHeight);
			}

			// Add price
			const priceText = `${t('price.total')}: ${formatNumber(calculatePrice({ state, prices }))} UAH`;
			const priceStyles = {
				fontSize: PDF_FONT_SIZES.X_LARGE,
				fontStyle: 'bold',
			} as const;

			const priceTextSize = pdfGenerator.getTextSize({
				text: priceText,
				...priceStyles,
			});

			pdfGenerator.addText({
				topMargin: 25,
				text: priceText,
				xPosition: fileSize.contentWidth - priceTextSize.width + PDF_X_PADDING,
				...priceStyles,
			});

			pdfGenerator.download(PDF_FILE_NAME);
		} catch {
			handleShowToast(t('toast.pdfError'));
		} finally {
			setIsLoading(false);
			handleShowToast(t('toast.pdf'));
		}
	};

	return {
		isLoading,
		handleExportPDF,
	};
};
